import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { AppComponent } from './app.component';
import { RouterModule } from '@angular/router';
import { ROUTES } from './app.routes';
import { HeaderComponent } from './header/header.component';
import { HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CustomMaterialModule } from '../ui-modules/custom-material.module';
import { AlertDialogComponent } from '../shared-module/dialogs/alert-dialog/alert-dialog.component';
import { PipeModule } from '../shared-module/common/pipes/pipe.module';
import { TranslationService } from '../shared-module/common/services/translation.service';
import { AuthHttpInterceptor } from '../shared-module/common/interseptors/http.interseptor';
import { TokenResolverService } from '../shared-module/common/resolvers/token-resolver.service';
import { SpinnerService } from '../app-module/spinner/spinner.service';
import { TranslationConstants } from '../shared-module/common/constants/translations.constats';
import { CurrentUser } from '../shared-module/common/services/current-user.service';
import { CurrentUserResolverService } from '../shared-module/common/resolvers/current-user-resolver.service';
import { MessagesService } from '../shared-module/common/services/messages.service';
import { TranslationsResolverService } from '../shared-module/common/resolvers/translations-resolver.service';
import { FooterComponent } from './footer/footer.component';
import { environment } from '../../environments/environment';
import { CpqSystemIdService } from '../shared-module/cpq-system-id/cpq-system-id.service';
import { CurrentTenantSettingsResolver } from '../shared-module/common/resolvers/tenant-settings-resolver.service';
import { TenantSettings } from '../shared-module/common/services/tenant-settings.service';
import { CommonTranslationResolverService } from '../shared-module/common/resolvers/init-resolver.service';
import { BlockedUsersService } from '../shared-module/common/services/blocked-users.service';
import { AuthGuard } from '../shared-module/common/guards/auth.guard';
import { ChangeSetService } from '../shared-module/common/services/change-set.service';
import { PlaybookModule } from '../playbook-module/playbook.module';
import { MenuNavBarModule } from './menu-nav-bar/menu-nav-bar.module';
import { DictionaryService } from '../shared-module/common/services/dictionary.service';
import { CpqGlobalErrorHandler } from '../shared-module/cpq-global-error-handler/cpq-global-error-handler';
import { LoggerService } from '../shared-module/cpq-global-error-handler/LoggerService.service';
import { MatTreeModule } from '@angular/material/tree';
import { CompanionService } from "../providers-module/companion/companion.service";
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';


@NgModule({ declarations: [
        AppComponent,
        HeaderComponent,
        AlertDialogComponent,
        FooterComponent
    ],
    exports: [AlertDialogComponent],
    bootstrap: [AppComponent],
    imports: [
        RouterModule.forRoot(ROUTES),
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        PipeModule,
        CustomMaterialModule,
        PlaybookModule,
        MenuNavBarModule,
        MatTreeModule
      ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: initApp,
            multi: true,
            deps: [TokenResolverService, CurrentUserResolverService, CurrentTenantSettingsResolver, CommonTranslationResolverService, HttpBackend]
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthHttpInterceptor,
            multi: true
        },
        {
            provide: ErrorHandler,
            useClass: CpqGlobalErrorHandler,
        },
        HttpClient,
        TranslationService,
        SpinnerService,
        CurrentUser,
        TokenResolverService,
        TranslationsResolverService,
        CurrentUserResolverService,
        CurrentTenantSettingsResolver,
        CommonTranslationResolverService,
        MessagesService,
        CpqSystemIdService,
        TenantSettings,
        BlockedUsersService,
        AuthGuard,
        ChangeSetService,
        DictionaryService,
        LoggerService,
        CompanionService,
        provideHttpClient(withInterceptorsFromDi()),
        // CDK v21+ uses the native Popover API (popover="manual" + showPopover()) for overlay
        // hosts, placing each dialog/menu in the browser's top layer. The top layer renders
        // above all CSS z-index stacking, so third-party tooltips (e.g. ngx-charts) that rely
        // on z-index to appear above dialogs are always painted behind them. Setting
        // usePopover: false reverts CDK to the pre-v21 z-index model, restoring the
        // expected rendering order without affecting any other overlay behaviour.
        { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } }
    ] })
export class AppModule {}

export function initApp(
  tokenResolverService: TokenResolverService,
  currentUserResolverService: CurrentUserResolverService,
  currentTenantSettingsResolver: CurrentTenantSettingsResolver,
  commonTranslationResolverService: CommonTranslationResolverService,
  httpBackend: HttpBackend
): () => Promise<any> {
  return () => {
    let http = new HttpClient(httpBackend)
    setRefreshSessionLoop(http, 10);
    return tokenResolverService.resolve().then(() => {
      return currentUserResolverService.resolve().then(() => {
        return currentTenantSettingsResolver.resolve().then(() => {
          removeTranslationsLocalStorage();
          return commonTranslationResolverService.resolve().then(() => {
          });
        });
      });
    });
  };
}

function removeTranslationsLocalStorage() {
  localStorage.removeItem(TranslationConstants.TRANSLATIONS);
  localStorage.removeItem(TranslationConstants.LIST_OF_TRANSLATED_MODULES);
}

/**
 *
 * @param http
 * @param interval in minutes
 */
function setRefreshSessionLoop(http: HttpClient, interval: number) {
  setInterval(() => {
    http.get(`${environment.url}/api/rd/v1/Core/RefreshSession`).subscribe(() => { }, (error) => alert(error));
  }, interval * 60 * 1000);
}
